<template>
  <button :type="type" :disabled="disabled || loading" :class="buttonClasses" @click="handleClick">
    <Icon v-if="loading" icon="svg-spinners:ring-resize" class="ui-button__loading-icon" />
    <img
      v-if="iconSrc && !loading && iconPosition === 'left'"
      :src="iconSrc"
      :class="iconClasses"
    />
    <Icon v-if="icon && !loading && iconPosition === 'left'" :icon="icon" :class="iconClasses" />
    <span v-if="$slots.default" :class="{ 'button-text': iconSrc || icon || loading }">
      <slot></slot>
    </span>
    <img
      v-if="iconSrc && !loading && iconPosition === 'right'"
      :src="iconSrc"
      :class="iconClasses"
    />
    <Icon v-if="icon && !loading && iconPosition === 'right'" :icon="icon" :class="iconClasses" />
  </button>
</template>

<script lang="ts" setup>
import { Icon } from "@iconify/vue";
import { computed } from "vue";
import type { ButtonType, Size, Variant } from "./types";

interface Props {
  variant?: Variant;
  size?: Size;
  type?: ButtonType;
  disabled?: boolean;
  loading?: boolean;
  icon?: string;
  iconSrc?: string;
  iconPosition?: "left" | "right";
  block?: boolean;
  rounded?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  variant: "primary",
  size: "md",
  type: "button",
  disabled: false,
  loading: false,
  iconPosition: "left",
  block: false,
  rounded: false,
});

const emit = defineEmits<{
  click: [event: MouseEvent];
}>();

const buttonClasses = computed(() => {
  const classes = ["ui-button"];

  // Variant
  classes.push(`ui-button--${props.variant}`);

  // Size
  classes.push(`ui-button--${props.size}`);

  // States
  if (props.disabled || props.loading) classes.push("ui-button--disabled");
  if (props.loading) classes.push("ui-button--loading");
  if (props.block) classes.push("ui-button--block");
  if (props.rounded) classes.push("ui-button--rounded");

  return classes;
});

const iconClasses = computed(() => {
  const classes = ["ui-button__icon"];
  if (props.iconPosition === "left") classes.push("ui-button__icon--left");
  if (props.iconPosition === "right") classes.push("ui-button__icon--right");
  return classes;
});

const handleClick = (event: MouseEvent) => {
  if (!props.disabled && !props.loading) {
    emit("click", event);
  }
};
</script>

<style scoped>
.ui-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-family: var(--font-family);
  font-weight: 400 !important;
  text-align: center;
  vertical-align: middle;
  cursor: pointer;
  user-select: none;
  border: 1px solid transparent;
  transition: all var(--transition-normal);
  white-space: nowrap;
  text-decoration: none;
}

.ui-button:focus-visible {
  outline: none !important;
  box-shadow: var(--ring) !important;
}

/* Size variants */
.ui-button--xs {
  padding: 4px 12px;
  font-size: var(--font-size-xs);
  font-weight: 400 !important;
  border-radius: calc(var(--radius) - 2px);
}

.ui-button--sm {
  padding: 4px 12px;
  font-size: var(--font-size-xs);
  font-weight: 400 !important;
  border-radius: calc(var(--radius) - 2px);
}

.ui-button--md {
  padding: 6px 14px;
  font-size: var(--font-size-xs);
  font-weight: 400 !important;
  border-radius: var(--radius);
}

.ui-button--lg {
  padding: 10px 20px;
  font-size: var(--font-size-xs);
  font-weight: 400 !important;
  border-radius: calc(var(--radius) + 1px);
}

.ui-button--xl {
  padding: 12px 24px;
  font-size: var(--font-size-base);
  border-radius: calc(var(--radius) + 2px);
}

/* Variants */
.ui-button--primary {
  background-color: var(--color-primary);
  border-color: var(--color-primary);
  color: var(--color-bg-white);
}

.ui-button--primary:hover:not(.ui-button--disabled) {
  background-color: var(--color-primary-dark);
  border-color: var(--color-primary-dark);
}

.ui-button--primary:focus-visible {
  box-shadow: var(--ring) !important;
}

.ui-button--outline {
  background-color: transparent;
  border-color: var(--color-border);
  color: #374151;
}

.ui-button--outline:hover:not(.ui-button--disabled) {
  background-color: var(--color-bg-muted);
  border-color: var(--color-text-muted);
  color: #111827;
}

.ui-button--secondary {
  background-color: transparent;
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.ui-button--secondary:hover:not(.ui-button--disabled) {
  background-color: var(--color-primary);
  color: var(--color-bg-white);
}

.ui-button--success {
  background-color: var(--color-success);
  border-color: var(--color-success);
  color: var(--color-bg-white);
}

.ui-button--success:hover:not(.ui-button--disabled) {
  background-color: #059669;
  border-color: #059669;
}

.ui-button--warning {
  background-color: var(--color-warning);
  border-color: var(--color-warning);
  color: var(--color-bg-white);
}

.ui-button--warning:hover:not(.ui-button--disabled) {
  background-color: #d97706;
  border-color: #d97706;
}

.ui-button--danger {
  background-color: var(--color-danger);
  border-color: var(--color-danger);
  color: var(--color-bg-white);
}

.ui-button--danger:hover:not(.ui-button--disabled) {
  background-color: #dc2626;
  border-color: #dc2626;
}

.ui-button--danger:focus-visible {
  box-shadow: var(--ring-danger) !important;
}

.ui-button--ghost {
  background-color: transparent;
  border-color: transparent;
  color: var(--color-primary);
}

.ui-button--ghost:hover:not(.ui-button--disabled) {
  background-color: var(--color-bg-selected);
}

/* States */
.ui-button--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.ui-button--block {
  width: 100%;
  display: flex;
}

.ui-button--rounded {
  border-radius: 9999px;
}

/* Icon */
.ui-button__icon {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.ui-button__icon--left {
  margin-right: 2px;
}

.ui-button__icon--right {
  margin-left: 2px;
}

.ui-button__icon img {
  height: 13px;
  width: auto;
}

.ui-button__icon .iconify {
  font-size: 0.8em;
}

.ui-button__loading-icon {
  display: inline-flex;
  align-items: center;
  font-size: 16px;
}
</style>
